import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogActions, Typography, Button, Grid2 } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers';

import { useAppState } from '../../state';
import { generateRandomId } from '../../utils';
import { apiPostMeetingCreate } from '../../utils/api';

type ScheduleMeetingModalProps = {
  onClose: () => void;
  open: boolean;
};

const getInitialFromDate = () => {
  const date = dayjs();
  const minute = date.get('m');
  return date.set('m', (Math.round(minute / 5) + 1) * 5);
};

const ScheduleMeetingModal: React.FC<ScheduleMeetingModalProps> = ({ onClose, open }) => {
  const { username, domain, setMeetingCreated } = useAppState();
  const [fromDate, setFromDate] = React.useState<Dayjs | null>(getInitialFromDate());
  const [toDate, setToDate] = React.useState<Dayjs | null>(getInitialFromDate().add(1, 'h'));

  useEffect(() => {
    setFromDate(getInitialFromDate());
    setToDate(getInitialFromDate().add(1, 'h'));
  }, [open]);

  // takes care of selecting different date in the from section and handling time choosing as well
  const handleFromDateChange = (date: Dayjs | null) => {
    if (!date) throw new Error('Invalid date argument');
    setFromDate(date);
    setToDate(date.add(1, 'h'));
  };

  // takes care of selecting different date in to section
  const handleToDateChange = (date: Dayjs | null) => {
    setToDate(date);
  };

  const handleCreateMeeting = () => {
    // name of system user in e-mail address format
    const fromupn = `${username}@${domain}.local`;
    // assigns random id for meeting created
    const meetingId = generateRandomId(11) + username.substr(0, 2) + domain.substr(0, 2);
    const startDate = fromDate!.toISOString();
    const endDate = toDate!.toISOString();
    // calls the apiPostMeetingCreate function
    apiPostMeetingCreate(fromupn, meetingId, startDate, endDate)
      // changes boolean status to true after posting data
      .then(res => res.json())
      .then(data => {
        setMeetingCreated(true);
        onClose();
      })
      .catch(e => {
        onClose();
      });
  };

  return (
    <Dialog open={open} fullWidth onClose={onClose}>
      <DialogContent>
        <Typography variant="h5">Schedule a meeting</Typography>
        <Grid2 display="grid" gridTemplateColumns="repeat(2,1fr)" gridTemplateRows="repeat(2,1fr)" gap={2} mt={2}>
          <DatePicker value={fromDate} onChange={handleFromDateChange} label="From" disablePast />
          <TimePicker value={fromDate} onChange={handleFromDateChange} label="Time" disablePast />
          <DatePicker value={toDate} onChange={handleToDateChange} label="To" disablePast />
          <TimePicker value={toDate} onChange={handleToDateChange} label="Time" disablePast />
        </Grid2>
      </DialogContent>
      <DialogActions>
        <Button type="submit" variant="contained" color="primary" onClick={handleCreateMeeting}>
          Create
        </Button>
        <Button type="submit" variant="outlined" color="primary" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleMeetingModal;
